import { Component, EventEmitter, HostListener, inject, Input, OnInit, Output } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { EDUCATION_LEVEL_LABEL_KEYS, EducationLevelEnum } from '@digilize/shared/definitions/src';
import { TranslateService } from '@ngx-translate/core';

interface PreviewSkill {
  skill_name: string;
  rating: number;
}

interface PreviewLanguage {
  skill_name: string;
  rating: number;
  is_native: boolean;
}

interface PreviewWorkHistory {
  position_name: string;
  education_name: string;
  start_date: string;
  end_date: string;
}

interface PreviewEducation {
  school_name: string;
  education_name: string;
  education_level: string;
  start_date: string;
  end_date: string;
}

interface PreviewData {
  name: string;
  email: string;
  city: string;
  knowledgeSkills: PreviewSkill[];
  softSkills: PreviewSkill[];
  languages: PreviewLanguage[];
  workHistory: PreviewWorkHistory[];
  education: PreviewEducation[];
}

@Component({
  selector: 'bwc-profile-preview',
  templateUrl: './profile-preview.component.html',
  styleUrls: ['./profile-preview.component.scss'],
})
export class ProfilePreviewComponent implements OnInit {
  @Input() form: UntypedFormGroup;
  @Output() closed = new EventEmitter<void>();

  private readonly translateService = inject(TranslateService);

  previewData: PreviewData;

  @HostListener('document:keydown.escape')
  onEscapeKey(): void {
    this.close();
  }

  ngOnInit(): void {
    const formValue = this.form?.value;
    this.previewData = {
      name: `${formValue?.first_name ?? ''} ${formValue?.last_name ?? ''}`.trim() || '—',
      email: formValue?.email ?? '—',
      city: formValue?.city ?? '—',
      knowledgeSkills: (formValue?.knowledge_skills ?? []).map((s: any) => ({
        skill_name: s.skill_name,
        rating: s.rating ?? 0,
      })),
      softSkills: (formValue?.soft_skills ?? []).map((s: any) => ({
        skill_name: s.skill_name,
        rating: s.rating ?? 0,
      })),
      languages: (formValue?.languages ?? []).map((l: any) => ({
        skill_name: l.skill_name,
        rating: l.rating ?? 0,
        is_native: l.is_native ?? false,
      })),
      workHistory: (formValue?.work_history ?? []).map((w: any) => ({
        position_name: w.position_name ?? '',
        education_name: w.education_name ?? '',
        start_date: w.start_date ?? '',
        end_date: w.end_date ?? '',
      })),
      education: (formValue?.education ?? []).map((e: any) => ({
        school_name: e.school_name ?? '',
        education_name: e.education_name ?? '',
        education_level: this.resolveEducationLevelLabel(e.education_level),
        start_date: e.start_date ?? '',
        end_date: e.end_date ?? '',
      })),
    };
  }

  close(): void {
    this.closed.emit();
  }

  onOverlayClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('preview-overlay')) {
      this.close();
    }
  }

  private resolveEducationLevelLabel(value: unknown): string {
    if (value === null || value === undefined || value === '') return '';
    const key = EDUCATION_LEVEL_LABEL_KEYS[Number(value) as EducationLevelEnum];
    return key ? this.translateService.instant(key) : '';
  }
}
